import { useEffect, useState } from "react";
import { ChevronRight, CreditCard, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";

type CardProviderResponse = components["schemas"]["CardProviderResponse"];
type CardProvider = components["schemas"]["CardProvider"];

export interface LinkCardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Called once a card is linked in-call (the sample provider), so the page can refresh its list.
  onLinked: () => void;
}

// Providers come from the API: they depend on the Client, and unconfigured ones show as unavailable, not hidden.
export function LinkCardDialog({ open, onOpenChange, onLinked }: LinkCardDialogProps) {
  const api = useDolfinClient();
  const [providers, setProviders] = useState<CardProviderResponse[] | null>(null);
  const [linking, setLinking] = useState<CardProvider | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setProviders(null);
    api.GET("/v1/card-providers", {}).then(({ data, error }) => {
      if (cancelled) return;
      if (error) toast.error(apiErrorMessage(error, "Couldn't load card providers."));
      setProviders(data ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [api, open]);

  async function link(provider: CardProvider) {
    setLinking(provider);
    const { data, error } = await api.POST("/v1/card-connections/enrollment-session", {
      body: { provider },
    });
    setLinking(null);

    if (error || !data) {
      toast.error(apiErrorMessage(error, "Couldn't start card enrollment."));
      return;
    }

    if (data.enrollmentCompleted) {
      toast.success("Sample card linked with its recent transactions.");
      onOpenChange(false);
      onLinked();
    } else if (data.url) {
      // The provider hosts the card capture, so the card number never reaches this app or Dolfin.
      window.open(data.url, "_blank", "noopener");
      toast.success("Finish enrolling the card, then sync to pull it in.");
      onOpenChange(false);
    } else {
      toast.success("Enrollment session created. Hand the token to the card provider's component.");
      onOpenChange(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Link a card</DialogTitle>
          <DialogDescription>
            Choose how to connect the card. Card details are captured by the provider, so no card number
            reaches Dolfin.
          </DialogDescription>
        </DialogHeader>

        {providers === null ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Spinner className="size-4" /> Loading providers...
          </div>
        ) : providers.length === 0 ? (
          <p className="py-6 text-sm text-muted-foreground">No card providers are available yet.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {providers.map((provider) => {
              const disabled = !provider.isAvailable || linking !== null;
              return (
                <button
                  key={provider.provider}
                  type="button"
                  disabled={disabled}
                  onClick={() => link(provider.provider!)}
                  className="flex items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
                >
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted">
                    {provider.isSample ? <Sparkles className="size-4" /> : <CreditCard className="size-4" />}
                  </span>
                  <span className="flex flex-1 flex-col gap-0.5">
                    <span className="flex items-center gap-2 text-sm font-medium">
                      {provider.name}
                      {provider.isSample ? <Badge variant="secondary">Sandbox</Badge> : null}
                      {!provider.isAvailable ? <Badge variant="outline">Not available yet</Badge> : null}
                    </span>
                    <span className="text-xs text-muted-foreground">{provider.description}</span>
                  </span>
                  {linking === provider.provider ? (
                    <Spinner className="size-4" />
                  ) : (
                    <ChevronRight className="size-4 text-muted-foreground" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
